import { Component, ElementRef, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isBoolean, isDefined, isString } from 'ionic-angular/util/util';
import { ControlComponent } from '../../control.component';
import { IToggleFieldValue, ToggleControl } from '../toggle-control.model';

@Component({
    selector: 'toggle',
    template: `
        <ion-list>
            <ion-item>
                <ion-toggle
                    [formControl]="formGroup.get('checked')"
                ></ion-toggle>
            </ion-item>
            <ion-item>
                <ion-textarea
                    [formControl]="formGroup.get('comment')"
                    placeholder="Comment"
                    maxlength="255"
                ></ion-textarea>
            </ion-item>
        </ion-list>
    `,
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ToggleControlComponent), multi: true},
        {provide: NG_VALIDATORS, useExisting: forwardRef(() => ToggleControlComponent), multi: true}
    ]
})
export class ToggleControlComponent extends ControlComponent {
    @Input() definition: ToggleControl;
    @Output() changes: EventEmitter<any> = new EventEmitter();

    formGroup = new FormGroup({
        checked: new FormControl(),
        comment: new FormControl()
    });

    constructor(
        protected elemRef: ElementRef
    ) {
        super(elemRef);

        this.formGroup.get('checked').valueChanges
            .takeUntil(this.ngUnsubscribe)
            .subscribe(values => this.updateChecked(values));

        this.formGroup.get('comment').valueChanges
            .takeUntil(this.ngUnsubscribe)
            .subscribe(values => this.updateComment(values));

    }

    /**
     * Returns the string that should be used in the comment if the user sets
     * the toggle to true
     */
    get commentToggleOn(): string {
        return this.getConfigOption('commentToggleOn');
    }

    /**
     * Returns the string that should be used in the comment if the user sets
     * the toggle to false
     */
    get commentToggleOff(): string {
        return this.getConfigOption('commentToggleOff');
    }

    /**
     * Return the default value from the config
     *
     * The reason this getter is required is that when the control config is
     * deserialized the defaultValue can actually be an empty string, thus we
     * check here if the defaultValue is empty string and if so return undefined
     */
    get defaultValue(): IToggleFieldValue {
        return this.definition.defaultValue === '' ?
            undefined :
            this.definition.defaultValue as IToggleFieldValue;
    }

    set value(value: IToggleFieldValue) {
        this._value = value;
    }

    get value(): IToggleFieldValue {
        return this._value;
    }

    /**
     * This method is a convenience method for setting the formGroup values
     * without emitting any change events
     * @param value {IToggleFieldValue}
     */
    setFormGroupValueWithoutEmit(value: IToggleFieldValue): void {
        this.formGroup.get('checked').setValue(
            value.checked,
            {emitEvent: false}
        );

        this.formGroup.get('comment').setValue(
            value.comment,
            {emitEvent: false}
        );
    }

    /**
     * Handle the user updating the checked form control
     * @param checked {boolean} the new value for checked
     * @protected
     */
    protected updateChecked(checked: boolean): void {

        let comment: string;

        comment = this.formGroup.value.comment;

        this.formGroup.get('checked').markAsDirty();

        // if the comment has not been modified by the user (either by restoring
        // from a saved value, or by updating it now) we should use the comment
        // from the config
        if (this.formGroup.get('comment').pristine) {
            comment = checked ?
                this.commentToggleOn :
                this.commentToggleOff;
            this.formGroup.get('comment').setValue(comment, {emitEvent: false});
        }

        this.updateValue({
            checked,
            comment
        });
    }

    /**
     * Handle the user updating the comment form control
     * @param comment {string} the new value for comment
     * @protected
     */
    protected updateComment(comment: string): void {
        this.formGroup.get('comment').markAsDirty();
        this.updateValue({
            checked: this.formGroup.value.checked,
            comment
        });
    }

    updateValue(value: IToggleFieldValue): void {
        super.updateValue(value);
    }

    /**
     * This method is called by Angular when our value accessor component is
     * being initialised. The value
     * @param savedValue
     */
    writeValue(savedValue: IToggleFieldValue): void {

        // check if we are handling an old version of the toggle control which
        // used to save the toggle value as a simple boolean. In that case we
        // upgrade the value to the new shape, save it a return
        if (isBoolean(savedValue)) {
            this.updateValue({
                checked: savedValue as any,
                comment: ''
            });
            this.setFormGroupValueWithoutEmit(this.value);
            this.formGroup.get('checked').markAsDirty();
            this.formGroup.get('comment').markAsDirty();
            return;
        }

        // if there was no previously saved value
        if (isString(savedValue) || savedValue === null || savedValue === undefined) {

            // if we have a default value, initialise the control with it
            if (isDefined(this.defaultValue)) {
                this.updateValue(this.defaultValue);
                this.setFormGroupValueWithoutEmit(this.defaultValue);
                this.formGroup.get('checked').markAsPristine();
                this.formGroup.get('comment').markAsPristine();
                return;
            }

            // otherwise initialise the control to the initial state
            this.updateValue({
                checked: false,
                comment: this.commentToggleOff
            });
            this.setFormGroupValueWithoutEmit(this.value);
            this.formGroup.get('checked').markAsPristine();
            this.formGroup.get('comment').markAsPristine();
            return;
        }

        this.updateValue(savedValue);
        this.setFormGroupValueWithoutEmit(savedValue);
        this.formGroup.get('checked').markAsDirty();
        this.formGroup.get('comment').markAsDirty();
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/toggle/form/toggle-control.component.ts